import React, { useMemo, useState } from 'react';
import clsx from 'clsx';

import { useStyles } from './style';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import CheckIcon from '@material-ui/icons/Check';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentServices } from '../PaymentServices';
import { PaymentCards } from '../PaymentCards';
import { SubscriptionActivatedPopup } from '../SubscriptionActivatedPopup';
import { OverlayModal } from '../../../../shared/components/overlay-modal';
import { PlanTypes } from '../CurrentPlan';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';
import { useGetStripeProductsQuery } from '../../../../api/stripe-product';
import { LocalStorageService } from '../../../../shared/services/localStorage.service';
import { formatCurrency } from '../../../../shared/helpers/format-currency';

interface Props {
  backStep: () => void;
  isYearly?: boolean;
  currentPlan: PlanTypes;
  newPlan: PlanTypes;
}

export const UpgradePlanSummary: React.FC<Props> = ({ backStep, currentPlan, newPlan, isYearly = true }) => {
  const classes = useStyles();
  const { isMobile } = useScreenSize();
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISABLE_KEY as string, { apiVersion: '2023-08-16' });
  const [activatedOpen, setActivatedOpen] = useState<boolean>(false);
  const countryCode = LocalStorageService.getItem('country') || 'au';
  const { data: products } = useGetStripeProductsQuery({country_code: countryCode});

  const stripePrice = useMemo(() => {
    const product = products?.stripe_products?.find(
      (p) => p.product_key === newPlan
    );

    if(!product) {
      return null;
    }

    const price = product.stripe_prices.find(
      (p) => p.interval === (isYearly ? 'year' : 'month')
    );

    if(!price) {
      return null;
    }

    return price;
  }, [isYearly, newPlan, products]);

  const productPrice = useMemo(() => {
    if(stripePrice === null) {
      return 0;
    }

    return stripePrice.unit_amount;
  }, [stripePrice]);

  const productCurrency = useMemo(() => {
    if(stripePrice === null) {
      return 'aud';
    }

    return stripePrice.currency;
  }, [stripePrice]);

  const onActivatedPopupClose = () => {
    setActivatedOpen(false);
    window.location.reload();
  };

  const billingTotalText = useMemo(() => {
    if(isYearly) {
      return `${formatCurrency(productPrice/100, 0, products?.price_country_code, products?.price_country_currency)} per year (Save 20%)`;
    }

    return `${formatCurrency(productPrice/100, 0, products?.price_country_code, products?.price_country_currency)} per month`;
  }, [isYearly, productPrice, products]);

  return (
    <Box className={classes.root}>
      {
        isMobile
          ? <>
              <Box className={classes.backContainer} onClick={backStep}>
                <ArrowBackIcon className={classes.backChevron} /> {
                  currentPlan === 'free'
                    ? 'Unlock Your Full Potential'
                    : 'Upgrade to Keep Orders Flowing'
                }
              </Box>
            </>
          : <>
              <Box className={classes.backContainer} onClick={backStep}>
                <ChevronLeftIcon className={classes.backChevron} /> Back
              </Box>
              <Typography className={classes.subHeader1}>
                {
                  currentPlan === 'free'
                    ? 'Unlock Your Full Potential'
                    : 'Upgrade to Keep Orders Flowing'
                }
              </Typography>
            </>
      }
      <Typography className={classes.subHeader2}>
        {
          currentPlan === 'free'
            ? `You're currently on the Free plan. Upgrade now to unlock more features, automate your workflow, and maximise your ordering efficiency.`
            : 'Your trial has almost finished - select a plan to continue receiving orders and unlock advanced features'
        }
      </Typography>
      <Box className={classes.billedSwitch}>
        <span className={clsx(classes.billedMonthly, !isYearly && classes.billedActive)}>
          Billed Monthly
        </span>
        <Switch
            checked={isYearly}
            disabled={true}
            color='primary'
            classes={{
              thumb: classes.thumb,
              track: classes.track,
              checked: classes.checked,
            }}
          />
        <span className={clsx(classes.billedAnnually, isYearly && classes.billedActive)}>
          Billed Annually <span className={classes.save20}>(SAVE 20%)</span>
        </span>
      </Box>

      <Grid container spacing={4} >
        <Grid item xs={12} md={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Box className={classes.bodyContainer}>
                <Typography className={classes.cardFeatureTitle}>
                  Your Plan Summary
                </Typography>
                <Typography className={classes.summaryRow}>
                  <span>Selected Plan:</span>{
                    newPlan === 'pro_lite'
                      ? <span>Pro - Lite Plan</span>
                      : <span>Pro - Standard Plan (Recommended)</span>}
                </Typography>
                <Typography className={classes.summaryRow}>
                  <span>Billing Total:</span><span>{billingTotalText}</span>
                </Typography>
                {isYearly && <Typography className={classes.summarySubRow}>
                  <span>Equivalent to {formatCurrency(productPrice/1200, 0, products?.price_country_code, products?.price_country_currency)}/month</span>
                </Typography>}
                <Box>
                  <Typography className={clsx(classes.summaryRow, classes.includesTitle)}>
                    Includes
                  </Typography>
                  <List dense className={classes.listItemWrapper}>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Boost sales with a branded online store (+ 24% moreproduct lines purshased)" className={classes.listItemText}/>
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Automate reordering and reduce order processing costs by up to 95%" className={classes.listItemText}/>
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Cut picking errors by 80% with smart digital fulfilment tools" className={classes.listItemText}/>
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Get insights from live margin and sales analytics" className={classes.listItemText}/>
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Sync with Xero, QuickBooks, MYOB, SAP and more" className={classes.listItemText}/>
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon className={classes.listItemIcon}>
                        <CheckIcon className={classes.checkIcon} />
                      </ListItemIcon>
                      <ListItemText primary="Optimise delivery runs and reduce fulfilment time" className={classes.listItemText}/>
                    </ListItem>
                  </List>
                </Box>
              </Box>
              <Box className={classes.paymentContainer}>
                <Typography className={classes.cardFeatureTitle}>
                  Set Up Payment
                </Typography>
                 <Elements
                    stripe={stripePromise}
                    options={{
                      paymentMethodCreation: 'manual',
                      mode: 'subscription',
                      amount: productPrice,
                      currency: productCurrency,
                      appearance: {
                        variables: {
                          borderRadius: '30px',
                        },
                      },
                    }}
                  >
                  <PaymentServices newPlan={newPlan} isYearly={isYearly} showActivatedPopup={() => setActivatedOpen(true)} />
                </Elements>
                <Box className={classes.formBoxWrap}>
                  <Elements stripe={stripePromise} >
                    <PaymentCards newPlan={newPlan} isYearly={isYearly} showActivatedPopup={() => setActivatedOpen(true)} />
                  </Elements>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <OverlayModal boxClassName={classes.modalBox} isOpen={activatedOpen} onClose={onActivatedPopupClose}>
        <SubscriptionActivatedPopup newPlan={newPlan} isYearly={isYearly} onClose={onActivatedPopupClose} />
      </OverlayModal>
    </Box>
)};
